/*
  Nische search - drives the header search drawer and the search page's own box.

  One custom element, <nische-search>, wraps a plain GET form to /search. It:
    - shows an "empty state" (popular searches, category tiles, the shopper's
      recent searches, recently viewed) before anything is typed,
    - fetches suggestions as the shopper types through Shopify's own
      predictive search, rendered server-side by `sections/nische-predictive-search.liquid`
      (`section_id=` on /search/suggest) - no JSON templating on the client,
    - handles arrow keys, Enter, Escape, clear and focus like a listbox.

  Weight: no libraries, no fonts. Requests are debounced, aborted when a newer
  keystroke lands, and cached per term for the life of the page.

  The header drawer's open/close, focus trap and body scroll lock stay with
  Dawn's `details-modal.js` - this file never opens or closes the <details>,
  it only reacts to it. Everything here degrades to the plain form.

  Storage keys:
    nische:searches - the shopper's last few terms (written on submit)
    nische:recent   - recently viewed products (written by the product page,
                      read here; same rows the collection rail uses)
*/
(function () {
  'use strict';

  if (window.NischeSearch) return;

  var SEARCH_KEY = 'nische:searches';
  var RECENT_KEY = 'nische:recent';
  var MAX_SEARCHES = 6;
  var DEBOUNCE = 200;

  function q(sel, root) {
    return (root || document).querySelector(sel);
  }
  function qa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function readStore(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* private mode: the feature is simply session-less */
    }
  }
  function readList(key) {
    try {
      var list = JSON.parse(readStore(key) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  /* ------------------------------------------------------ recent searches */

  function rememberSearch(term) {
    term = String(term || '').trim();
    if (!term) return;
    var list = readList(SEARCH_KEY).filter(function (t) {
      return typeof t === 'string' && t.toLowerCase() !== term.toLowerCase();
    });
    list.unshift(term);
    writeStore(SEARCH_KEY, JSON.stringify(list.slice(0, MAX_SEARCHES)));
  }

  function forgetSearch(term) {
    var list = readList(SEARCH_KEY).filter(function (t) {
      return t !== term;
    });
    writeStore(SEARCH_KEY, JSON.stringify(list));
  }

  /* ---------------------------------------------------------- the element */

  var proto = {};

  proto.connectedCallback = function () {
    if (this.nshBound) return;
    this.nshBound = true;

    this.form = q('form', this);
    this.input = q('[data-nsh-input]', this);
    this.panel = q('[data-nsh-panel]', this);
    this.empty = q('[data-nsh-empty]', this);
    this.results = q('[data-nsh-results]', this);
    this.status = q('[data-nsh-status]', this);
    this.clearBtn = q('[data-nsh-clear]', this);
    this.mode = this.getAttribute('data-mode') || 'header';
    this.details = this.closest('details');
    this.endpoint = this.getAttribute('data-url') || '/search/suggest';
    this.sectionId = this.getAttribute('data-section') || 'nische-predictive-search';
    this.limit = parseInt(this.getAttribute('data-limit'), 10) || 8;
    this.predictive = this.getAttribute('data-predictive') !== 'false';
    this.cache = new Map();
    this.controller = null;
    this.seq = 0;
    this.term = '';

    if (!this.form || !this.input) return;

    this.search = debounce(this.searchNow.bind(this), DEBOUNCE);
    this.bind();
    this.paintRecents();
    this.paintViewed();
    this.syncClear();

    // Page mode with a term already in the box (the results page): leave the
    // panel closed until the shopper interacts, the grid below IS the result.
    if (this.mode === 'header' && this.details && this.details.open) this.onOpen();
  };

  proto.bind = function () {
    var self = this;

    this.input.addEventListener('input', function () {
      self.syncClear();
      self.search();
    });

    this.input.addEventListener('focus', function () {
      if (self.mode === 'page') self.openPanel();
    });

    this.input.addEventListener('keydown', this.onKeydown.bind(this));

    this.form.addEventListener('submit', function (e) {
      var term = self.input.value.trim();
      if (!term) {
        e.preventDefault();
        self.input.focus();
        return;
      }
      // Enter while an option is highlighted follows that option instead.
      var active = q('[data-nsh-option][aria-selected="true"]', self);
      if (active) {
        e.preventDefault();
        self.follow(active);
        return;
      }
      rememberSearch(term);
    });

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.input.value = '';
        self.term = '';
        self.syncClear();
        self.showEmpty();
        self.input.focus();
      });
    }

    // Anything inside the panel that carries a term runs that search.
    this.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-nsh-term]');
      if (chip && self.contains(chip)) {
        e.preventDefault();
        self.runTerm(chip.getAttribute('data-nsh-term'));
        return;
      }
      var forget = e.target.closest('[data-nsh-forget]');
      if (forget && self.contains(forget)) {
        e.preventDefault();
        e.stopPropagation();
        forgetSearch(forget.getAttribute('data-nsh-forget'));
        self.paintRecents();
        return;
      }
      var clearAll = e.target.closest('[data-nsh-forget-all]');
      if (clearAll && self.contains(clearAll)) {
        e.preventDefault();
        writeStore(SEARCH_KEY, '[]');
        self.paintRecents();
        return;
      }
      var opt = e.target.closest('[data-nsh-option]');
      if (opt && self.contains(opt) && opt.tagName === 'A') rememberSearch(self.input.value.trim());
    });

    // A heart tap must not pull focus out of the input: the shopper keeps
    // typing (and the arrow keys keep working) after saving a piece.
    this.addEventListener('mousedown', function (e) {
      if (e.target.closest('[data-nc-wish]')) e.preventDefault();
    });

    // Moving the mouse over an option highlights it, so keyboard and mouse
    // never disagree about which row Enter would open. `mousemove` with a
    // position check, not `mouseover`: when the arrow keys scroll the panel
    // under a resting pointer, mouseover fires and would steal the highlight
    // straight back.
    var lastX = -1;
    var lastY = -1;
    this.addEventListener('mousemove', function (e) {
      if (e.clientX === lastX && e.clientY === lastY) return;
      lastX = e.clientX;
      lastY = e.clientY;
      var opt = e.target.closest('[data-nsh-option]');
      if (opt && self.contains(opt)) self.select(opt, false);
    });

    if (this.mode === 'header' && this.details) {
      this.details.addEventListener('toggle', function () {
        if (self.details.open) self.onOpen();
        else self.onClose();
      });
    }

    if (this.mode === 'page') {
      document.addEventListener('click', function (e) {
        if (!self.contains(e.target)) self.closePanel();
      });
      this.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          self.closePanel();
          self.input.blur();
        }
      });
    }
  };

  /* --------------------------------------------------------- open / close */

  proto.onOpen = function () {
    this.paintRecents();
    this.paintViewed();
    this.sizePanel();
    this.openPanel();
    // The shopper may have typed before this script arrived.
    if (this.input.value.trim()) this.searchNow();
  };

  proto.onClose = function () {
    if (this.controller) this.controller.abort();
    this.closePanel();
  };

  proto.openPanel = function () {
    if (!this.panel) return;
    this.panel.hidden = false;
    this.setAttribute('data-open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    if (this.mode === 'page') this.sizePagePanel();
  };

  /*
    Page mode. The box sits well below the header, so a dropdown of fixed
    height would run off the bottom of a phone and the shopper would have to
    scroll the page to see its end. Instead: bring the box up under the sticky
    header, then let the panel take exactly the room left to the bottom of the
    (visual) viewport - it scrolls inside itself, the "see all" button stays
    pinned. Re-measured on resize, on the phone keyboard opening, and on page
    scroll; scrolled out of sight, it closes.
  */
  proto.sizePagePanel = function () {
    var self = this;
    var header = document.querySelector('.shopify-section-header-sticky') || document.querySelector('.section-header');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var measure = function () {
      if (!self.panel || self.panel.hidden) return;
      var top = self.panel.getBoundingClientRect().top;
      var vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      var room = Math.floor(vh - top - 12);
      self.panel.style.maxHeight = Math.max(room, 160) + 'px';
    };

    // Scroll the box up under the header only when it is lower than that.
    var headerH = header ? header.getBoundingClientRect().height : 0;
    var wanted = headerH + 12;
    var boxTop = this.getBoundingClientRect().top;
    if (boxTop > wanted + 8) {
      window.scrollTo({ top: window.scrollY + boxTop - wanted, behavior: reduce ? 'auto' : 'smooth' });
      window.setTimeout(measure, reduce ? 0 : 420);
    }
    measure();

    if (this.pageSizeBound) return;
    this.pageSizeBound = true;
    var re = debounce(function () {
      if (!self.panel || self.panel.hidden) return;
      // Box scrolled off the top: the panel has nothing to hang from.
      if (self.getBoundingClientRect().bottom < 0) {
        self.closePanel();
        return;
      }
      measure();
    }, 100);
    window.addEventListener('scroll', re, { passive: true });
    window.addEventListener('resize', re);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', re);
  };

  proto.closePanel = function () {
    if (!this.panel) return;
    this.panel.hidden = true;
    this.removeAttribute('data-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.select(null);
  };

  /*
    The header drawer lives inside a sticky header, so the panel below it
    cannot know how tall the viewport is from CSS alone. Measure once per open
    (and on resize) so the panel scrolls inside itself instead of running off
    the bottom of a phone.
  */
  proto.sizePanel = function () {
    if (!this.panel || this.mode !== 'header') return;
    var self = this;
    // The panel and Dawn's modal both animate in from above, so their own
    // rects lie for the first ~300 ms. The header wrapper does not move:
    // the panel starts exactly at its bottom edge.
    var anchor = this.closest('.header-wrapper') || this.closest('.shopify-section-header') || this.panel;
    var apply = function () {
      var top = anchor === self.panel ? self.panel.getBoundingClientRect().top : anchor.getBoundingClientRect().bottom;
      var vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      var room = Math.floor(vh - top);
      if (room > 120) self.panel.style.maxHeight = room + 'px';
    };
    window.requestAnimationFrame(apply);
    window.setTimeout(apply, 350);
    if (!this.resizeBound) {
      this.resizeBound = true;
      var re = debounce(apply, 150);
      window.addEventListener('resize', re);
      if (window.visualViewport) window.visualViewport.addEventListener('resize', re);
    }
  };

  proto.syncClear = function () {
    if (!this.clearBtn) return;
    this.clearBtn.hidden = !this.input.value.length;
  };

  /* ------------------------------------------------------------ searching */

  proto.runTerm = function (term) {
    this.input.value = term;
    this.syncClear();
    rememberSearch(term);
    this.form.submit();
  };

  proto.searchNow = function () {
    var term = this.input.value.trim();
    if (term === this.term) return;
    this.term = term;

    if (!term) {
      if (this.controller) this.controller.abort();
      this.showEmpty();
      return;
    }
    if (!this.predictive || !this.results) return;

    var self = this;
    var key = term.toLowerCase();
    this.openPanel();

    if (this.cache.has(key)) {
      this.paint(this.cache.get(key));
      return;
    }

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    var seq = ++this.seq;
    this.setAttribute('data-busy', 'true');

    var url =
      this.endpoint +
      '?q=' +
      encodeURIComponent(term) +
      '&section_id=' +
      encodeURIComponent(this.sectionId) +
      '&resources[type]=product,collection,query,article,page' +
      '&resources[limit]=' +
      this.limit +
      '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,tag';

    fetch(url, { signal: this.controller.signal, credentials: 'same-origin' })
      .then(function (res) {
        // A bad section id answers 404 with an empty body - check the status.
        if (!res.ok) throw new Error('suggest ' + res.status);
        return res.text();
      })
      .then(function (html) {
        if (seq !== self.seq) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.getElementById('nsh-results');
        var markup = inner ? inner.innerHTML : '';
        self.cache.set(key, markup);
        self.paint(markup);
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        self.removeAttribute('data-busy');
        // The form still submits; the shopper just gets no live suggestions.
      });
  };

  proto.paint = function (markup) {
    this.removeAttribute('data-busy');
    if (this.empty) this.empty.hidden = true;
    this.results.hidden = false;
    this.results.innerHTML = markup;
    this.select(null);
    if (window.NischeWishlist && window.NischeWishlist.hydrate) window.NischeWishlist.hydrate(this.results);

    var live = q('[data-nsh-live]', this.results);
    if (this.status && live) this.status.textContent = live.textContent;
  };

  proto.showEmpty = function () {
    this.removeAttribute('data-busy');
    if (this.results) {
      this.results.hidden = true;
      this.results.innerHTML = '';
    }
    if (this.empty) this.empty.hidden = false;
    this.select(null);
    if (this.status) this.status.textContent = '';
  };

  /* ------------------------------------------------------------- keyboard */

  proto.options = function () {
    return qa('[data-nsh-option]', this).filter(function (el) {
      return el.offsetParent !== null;
    });
  };

  proto.select = function (el, scroll) {
    qa('[data-nsh-option][aria-selected="true"]', this).forEach(function (o) {
      o.setAttribute('aria-selected', 'false');
    });
    if (el) {
      el.setAttribute('aria-selected', 'true');
      if (el.id) this.input.setAttribute('aria-activedescendant', el.id);
      if (scroll !== false && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  };

  proto.onKeydown = function (e) {
    // Escape is Dawn's: details-modal.js closes the drawer on keyup. In page
    // mode the element-level listener closes the panel.
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    var opts = this.options();
    if (!opts.length) return;
    e.preventDefault();
    var current = q('[data-nsh-option][aria-selected="true"]', this);
    var i = opts.indexOf(current);
    if (e.key === 'ArrowDown') i = i + 1 >= opts.length ? 0 : i + 1;
    else i = i - 1 < 0 ? opts.length - 1 : i - 1;
    this.select(opts[i]);
  };

  proto.follow = function (el) {
    var term = this.input.value.trim();
    if (el.tagName === 'A' && el.href) {
      rememberSearch(term);
      window.location.href = el.href;
    } else if (el.getAttribute('data-nsh-term')) {
      this.runTerm(el.getAttribute('data-nsh-term'));
    } else {
      // The "see all" button: submit without re-entering the submit handler.
      rememberSearch(term);
      this.form.submit();
    }
  };

  /* ---------------------------------------------------- the empty state */

  proto.paintRecents = function () {
    var box = q('[data-nsh-recent]', this);
    if (!box) return;
    var list = q('[data-nsh-recent-list]', box);
    var terms = readList(SEARCH_KEY).filter(function (t) {
      return typeof t === 'string' && t;
    });
    if (!terms.length || !list) {
      if (list) list.innerHTML = '';
      box.hidden = true;
      return;
    }
    list.innerHTML = terms
      .map(function (t) {
        return (
          '<li class="nsh-chip nsh-chip--recent">' +
          '<button type="button" class="nsh-chip__go" data-nsh-term="' +
          escapeHtml(t) +
          '">' +
          '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.4"/><path d="M8 4.5V8l2.5 1.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
          '<span>' +
          escapeHtml(t) +
          '</span></button>' +
          '<button type="button" class="nsh-chip__x" data-nsh-forget="' +
          escapeHtml(t) +
          '" aria-label="Remove ' +
          escapeHtml(t) +
          ' from recent searches">' +
          '<svg viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
          '</button></li>'
        );
      })
      .join('');
    box.hidden = false;
  };

  proto.paintViewed = function () {
    var box = q('[data-nsh-viewed]', this);
    if (!box) return;
    var list = q('[data-nsh-viewed-list]', box);
    var count = parseInt(box.getAttribute('data-nsh-count'), 10) || 4;
    var here = window.location.pathname;
    var rows = readList(RECENT_KEY)
      .filter(function (r) {
        // Not the product the shopper is already looking at.
        return r && r.url && r.title && r.url !== here;
      })
      .slice(0, count);
    if (!rows.length || !list) {
      box.hidden = true;
      return;
    }
    var showHeart = box.getAttribute('data-nsh-hearts') !== 'false';
    // Same card as a suggestion (markup + classes), so the two rows match.
    list.innerHTML = rows
      .map(function (r) {
        var img = r.img
          ? '<img src="' +
            escapeHtml(r.img) +
            '"' +
            (r.srcset ? ' srcset="' + escapeHtml(r.srcset) + '"' : '') +
            ' sizes="(min-width: 990px) 160px, 124px" alt="" loading="lazy" width="160" height="160">'
          : '';
        var off = r.off ? '<span class="nsh-prod__off">' + escapeHtml(String(r.off).toLowerCase()) + '</span>' : '';
        var price = '<span class="nsh-prod__price">' + escapeHtml(r.price || '') + '</span>';
        if (r.was) price += '<s class="nsh-prod__was">' + escapeHtml(r.was) + '</s>';
        var handle = (String(r.url).match(/\/products\/([^/?#]+)/) || [])[1] || '';
        var heart =
          showHeart && handle
            ? '<button type="button" class="nsh-wish" data-nc-wish="' +
              escapeHtml(handle) +
              '" data-label-on="Remove ' +
              escapeHtml(r.title) +
              ' from wishlist" data-label-off="Save ' +
              escapeHtml(r.title) +
              ' to wishlist" aria-pressed="false" aria-label="Save ' +
              escapeHtml(r.title) +
              ' to wishlist" tabindex="-1">' +
              '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg></button>'
            : '';
        return (
          '<li class="nsh-prods__item"><a class="nsh-prod nsh-prod--mini" href="' +
          escapeHtml(r.url) +
          '"><span class="nsh-prod__media">' +
          img +
          off +
          '</span><span class="nsh-prod__body"><span class="nsh-prod__title">' +
          escapeHtml(r.title) +
          '</span><span class="nsh-prod__pricerow">' +
          price +
          '</span></span></a>' +
          heart +
          '</li>'
        );
      })
      .join('');
    if (window.NischeWishlist && window.NischeWishlist.hydrate) window.NischeWishlist.hydrate(list);
    box.hidden = false;
  };

  /* ------------------------------------------------- the results page */

  /*
    "From the Journal" on the search page. Articles and pages come from the
    predictive endpoint's JSON, which searches the whole store rather than the
    current page of results, so a guide still shows up when it ranks 30th.
    One small request, only when a search was actually performed.
  */
  function mountJournal(box) {
    var term = box.getAttribute('data-nsh-q');
    if (!term) return;
    var list = q('[data-nsh-journal-list]', box);
    var url =
      (box.getAttribute('data-nsh-url') || '/search/suggest') +
      '.json?q=' +
      encodeURIComponent(term) +
      '&resources[type]=article,page&resources[limit]=6';

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (!data || !data.resources || !data.resources.results) return;
        var r = data.resources.results;
        var rows = (r.articles || []).concat(r.pages || []);
        if (!rows.length || !list) return;
        list.innerHTML = rows
          .map(function (row) {
            var isArticle = !!row.published_at || !!row.author;
            var img = row.image
              ? '<img src="' + escapeHtml(row.image) + '" alt="" loading="lazy" width="96" height="96">'
              : '';
            return (
              '<li><a class="nsh-post' +
              (img ? '' : ' nsh-post--noimg') +
              '" href="' +
              escapeHtml(row.url) +
              '">' +
              (img ? '<span class="nsh-post__media">' + img + '</span>' : '') +
              '<span class="nsh-post__body"><span class="nsh-post__kind">' +
              (isArticle ? 'Journal' : 'Page') +
              '</span><span class="nsh-post__title">' +
              escapeHtml(row.title) +
              '</span></span></a></li>'
            );
          })
          .join('');
        box.hidden = false;
      })
      .catch(function () {
        /* the grid is the answer; the strip is a bonus */
      });
  }

  /* ----------------------------------------------------------------- boot */

  /*
    Back/forward cache. Submit from the drawer, land on the results, press
    Back: the browser can restore this page exactly as it was - drawer open,
    body scroll-locked by details-modal.js - with no click to undo it. Reset
    both on a restored page.
  */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    qa('.header__search details[open]').forEach(function (d) {
      d.removeAttribute('open');
    });
    document.body.classList.remove('overflow-hidden');
  });

  function boot() {
    qa('[data-nsh-journal]').forEach(function (box) {
      if (box.nshBound) return;
      box.nshBound = true;
      mountJournal(box);
    });
  }

  if (window.customElements && !window.customElements.get('nische-search')) {
    class NischeSearchElement extends HTMLElement {}
    Object.keys(proto).forEach(function (k) {
      NischeSearchElement.prototype[k] = proto[k];
    });
    window.customElements.define('nische-search', NischeSearchElement);
  }

  window.NischeSearch = { remember: rememberSearch };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
